<template>
                <div v-if="currentView === 'server_dns'">
                    <div class="page-header"><div><div class="page-title">DNS 服務管理</div><div class="page-subtitle">管理網域 DNS 記錄與驗證設定</div></div></div>
                    <div class="card p-6 mb-6">
                        <h3 class="card-title mb-3">管理網域 DNS</h3>
                        <select v-model="selectedDnsDomain" @change="fetchDnsRecords"
                            class="input md:w-1/2">
                            <option :value="null">請選擇網域...</option>
                            <option v-for="(d, idx) in safeArr(domains)" :key="keyById(d, 'domain-opt6', idx)" :value="d.id">{{ d.name }}</option>
                        </select>
                    </div>

                    <div v-if="selectedDnsDomain" class="card p-6">
                        <h3 class="card-title mb-4">DNS 記錄列表</h3>

                        <!-- Add Record -->
                        <div class="mb-4 flex gap-2 items-end flex-wrap">
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">類型</label>
                                <select v-model="newDnsRecord.type" class="input w-24">
                                    <option value="A">A</option>
                                    <option value="MX">MX</option>
                                    <option value="CNAME">CNAME</option>
                                    <option value="TXT">TXT</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">主機名 (@ 為根)</label>
                                <input v-model="newDnsRecord.host" placeholder="@" class="input w-32">
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">值</label>
                                <input v-model="newDnsRecord.value" placeholder="1.2.3.4"
                                    class="input w-48">
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">優先權 (MX)</label>
                                <input v-model="newDnsRecord.priority" type="number" class="input w-20"
                                    min="0" max="65535" :disabled="newDnsRecord.type !== 'MX'">
                            </div>
                            <button @click="addDnsRecord"
                                class="btn btn-primary mb-0.5">新增</button>
                        </div>

                        <table class="data-table">
                            <thead>
                                <tr class="border-b">
                                    <th class="p-2">類型</th>
                                    <th class="p-2">主機名</th>
                                    <th class="p-2">值</th>
                                    <th class="p-2">優先權</th>
                                    <th class="p-2">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(rec, idx) in safeArr(dnsRecords)" :key="keyById(rec, 'dns', idx)" class="border-b">
                                    <td class="p-2 font-bold">{{ rec.type }}</td>
                                    <td class="p-2">{{ rec.host }}</td>
                                    <td class="p-2 font-mono text-sm">{{ rec.value }}</td>
                                    <td class="p-2">{{ rec.priority || '-' }}</td>
                                    <td class="p-2">
                                        <button @click="deleteDnsRecord(rec.id)"
                                            class="btn btn-ghost btn-xs" style="color:var(--color-danger)">刪除</button>
                                    </td>
                                </tr>
                                <tr v-if="dnsRecords.length === 0">
                                    <td colspan="5" class="p-4 text-center" style="color:var(--gray-500);">尚無記錄。</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Server: Database -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  selectedDnsDomain, dnsRecords, newDnsRecord,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  fetchDnsRecords, addDnsRecord, deleteDnsRecord,
} = store
</script>

